import { Button, Switch, TextInput } from '@mantine/core';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiKeyResponse } from '@/api';
import { CodeBlock } from '@/components/Ui/CodeBlock';
import { ConfirmDelete } from '@/components/Ui/ConfirmDelete';
import { CopyButton } from '@/components/Ui/CopyButton';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Section, SectionBody } from '@/components/Ui/Section';
import { useUndoToast } from '@/components/Ui/Toast';
import { parseTimestamp, relativeTime, shortDate } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import { errorMessage } from '../format';
import {
  ALLOWED_LEVELS,
  EMPTY_MATRIX,
  isFullScope,
  matrixToScope,
  SCOPE_LEVELS,
  SCOPE_RESOURCES,
  type ScopeMatrix,
  scopeToMatrix,
} from './api-key-scope';
import classes from './Developers.module.css';

const EXPIRY_OPTIONS = ['30', '90', '365', 'never'] as const;
const DEFAULT_MATRIX: ScopeMatrix = { ...EMPTY_MATRIX, forms: 'read', messages: 'read' };

export function ApiKeysSection() {
  const { t, i18n } = useTranslation();
  const toast = useUndoToast();
  const api = useRustyState.getState().api;

  const [keys, setKeys] = useState<ApiKeyResponse[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [matrix, setMatrix] = useState<ScopeMatrix>(DEFAULT_MATRIX);
  const [expiry, setExpiry] = useState<(typeof EXPIRY_OPTIONS)[number]>('30');
  const [errors, setErrors] = useState<{ name?: string; form?: string }>({});
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setKeys(await useRustyState.getState().api.listApiKeys());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
    // Keys are used from elsewhere; "Last used" catches up when the tab is back in focus.
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const resetForm = () => {
    setName('');
    setMatrix(DEFAULT_MATRIX);
    setExpiry('30');
    setErrors({});
    setCreated(null);
  };

  const toggleForm = () => {
    if (formOpen) resetForm();
    setFormOpen(!formOpen);
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (name.trim().length < 3) next.name = t('developers.keys.nameTooShort');
    setErrors(next);
    if (Object.keys(next).length) return;
    setCreating(true);
    try {
      const res = await api.createApiKey(
        {
          name: name.trim(),
          scope: matrixToScope(matrix),
          expires_in_days: expiry === 'never' ? null : Number(expiry),
        },
        { onSuccess: false, onError: false }
      );
      setCreated(res.key ?? null);
      await load();
    } catch (err) {
      setErrors({ form: errorMessage(err) ?? t('developers.keys.createFailed') });
    } finally {
      setCreating(false);
    }
  };

  const setActive = async (key: ApiKeyResponse, active: boolean) => {
    setKeys(
      (prev) => prev?.map((k) => (k.id === key.id ? { ...k, is_active: active } : k)) ?? prev
    );
    try {
      await api.updateApiKey(key.id, { is_active: active }, { onSuccess: false });
      void load();
      toast(
        active
          ? t('developers.keys.activated', { name: key.name })
          : t('developers.keys.deactivated', { name: key.name }),
        async () => {
          await api.updateApiKey(key.id, { is_active: !active }, { onSuccess: false });
          await load();
        }
      );
    } catch {
      await load();
    }
  };

  const canDo = (key: ApiKeyResponse) => {
    if (isFullScope(key.scope)) return t('developers.keys.everything');
    const m = scopeToMatrix(key.scope);
    const parts = SCOPE_RESOURCES.filter((r) => m[r] !== 'none').map((r) =>
      t('developers.keys.canDoPart', {
        what: t(`developers.keys.resource.${r}`),
        level: t(`developers.keys.level.${m[r]}`).toLowerCase(),
      })
    );
    return parts.length ? parts.join(' · ') : t('developers.keys.nothing');
  };

  const isExpired = (key: ApiKeyResponse) =>
    !!key.expires_at && parseTimestamp(key.expires_at).getTime() < Date.now();

  return (
    <Section
      title={t('developers.keys.title')}
      actions={
        <Button size="xs" variant="default" aria-expanded={formOpen} onClick={toggleForm}>
          {formOpen ? t('ui.close') : t('developers.keys.new')}
        </Button>
      }
    >
      {formOpen && (
        <div className={shared.formArea}>
          {created ? (
            <div className={shared.stack} style={{ gap: 10 }}>
              <strong>{t('developers.keys.copyNow')}</strong>
              <div className={shared.row}>
                <code className={shared.secret}>{created}</code>
                <CopyButton text={created} />
              </div>
              <span className={shared.sub}>{t('developers.keys.sendWith')}</span>
              <CodeBlock code={`Authorization: Bearer ${created}`} wrap />
              <Button
                size="xs"
                variant="default"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  resetForm();
                  setFormOpen(false);
                }}
              >
                {t('developers.keys.done')}
              </Button>
            </div>
          ) : (
            <form className={shared.stack} style={{ gap: 16 }} onSubmit={create} noValidate>
              <TextInput
                label={t('developers.keys.name')}
                placeholder={t('developers.keys.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                error={errors.name}
                maw={420}
                disabled={creating}
              />
              <fieldset className={classes.fieldset}>
                <legend className={classes.legend}>{t('developers.keys.canDoLegend')}</legend>
                <div className={classes.matrix} role="presentation">
                  <span className={classes.matrixHead} />
                  {SCOPE_LEVELS.map((l) => (
                    <span key={l} className={classes.matrixHead}>
                      {t(`developers.keys.level.${l}`)}
                    </span>
                  ))}
                  {SCOPE_RESOURCES.map((r) => (
                    <div key={r} className={classes.matrixRow}>
                      <span>{t(`developers.keys.resource.${r}`)}</span>
                      {SCOPE_LEVELS.map((l) => (
                        <span key={l} className={classes.matrixCell}>
                          {ALLOWED_LEVELS[r].includes(l) && (
                            <input
                              type="radio"
                              name={`scope-${r}`}
                              checked={matrix[r] === l}
                              onChange={() => setMatrix((m) => ({ ...m, [r]: l }))}
                              aria-label={t('developers.keys.radioLabel', {
                                what: t(`developers.keys.resource.${r}`),
                                level: t(`developers.keys.level.${l}`).toLowerCase(),
                              })}
                              disabled={creating}
                            />
                          )}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
                <span className={shared.help}>{t('developers.keys.canDoHelp')}</span>
              </fieldset>
              <label className={shared.stack} style={{ gap: 6, maxWidth: 302 }}>
                <span style={{ fontWeight: 500, fontSize: 14 }}>
                  {t('developers.keys.expires')}
                </span>
                <select
                  className={shared.nativeInput}
                  value={expiry}
                  onChange={(e) =>
                    setExpiry(e.currentTarget.value as (typeof EXPIRY_OPTIONS)[number])
                  }
                  disabled={creating}
                >
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {t(`developers.keys.expiry.${o}`)}
                    </option>
                  ))}
                </select>
              </label>
              {errors.form && (
                <p className={shared.errorText} role="alert">
                  {errors.form}
                </p>
              )}
              <div className={shared.row}>
                <Button type="submit" loading={creating}>
                  {t('developers.keys.create')}
                </Button>
                <Button variant="default" onClick={toggleForm} disabled={creating}>
                  {t('ui.cancel')}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}

      {failed ? (
        <SectionBody>
          <p className={shared.errorText}>{t('developers.keys.loadFailed')}</p>
        </SectionBody>
      ) : keys === null ? (
        <SectionBody>
          <EmptyText>{t('ui.loading')}</EmptyText>
        </SectionBody>
      ) : (
        <DataTable
          caption={t('developers.keys.title')}
          rows={keys}
          rowKey={(k) => k.id}
          empty={t('developers.keys.empty')}
          columns={[
            {
              key: 'name',
              header: t('developers.keys.colName'),
              render: (k) =>
                k.is_active && !isExpired(k) ? (
                  <strong>{k.name}</strong>
                ) : (
                  <span className={shared.sub}>{k.name}</span>
                ),
            },
            { key: 'can', header: t('developers.keys.colCanDo'), render: canDo },
            {
              key: 'expires',
              header: t('developers.keys.colExpires'),
              render: (k) =>
                !k.expires_at
                  ? t('developers.keys.never')
                  : isExpired(k)
                    ? t('developers.keys.expired', { day: shortDate(k.expires_at, i18n.language) })
                    : shortDate(k.expires_at, i18n.language),
            },
            {
              key: 'used',
              header: t('developers.keys.colLastUsed'),
              render: (k) =>
                k.last_used_at
                  ? relativeTime(k.last_used_at, i18n.language)
                  : t('developers.keys.notUsed'),
            },
            {
              key: 'active',
              header: t('developers.keys.colActive'),
              render: (k) => (
                <Switch
                  checked={k.is_active}
                  onChange={(e) => void setActive(k, e.currentTarget.checked)}
                  aria-label={t('developers.keys.activeLabel', { name: k.name })}
                />
              ),
            },
            {
              key: 'delete',
              header: (
                <span className={shared.visuallyHidden}>{t('developers.keys.colDelete')}</span>
              ),
              render: (k) => (
                <ConfirmDelete
                  label={t('developers.keys.delete')}
                  variant="subtle"
                  size="xs"
                  description={t('developers.keys.deleteBody', { name: k.name })}
                  confirmLabel={t('developers.keys.deleteConfirm')}
                  onConfirm={async () => {
                    await api.deleteApiKey(k.id);
                    toast(t('developers.keys.deleted', { name: k.name }));
                    await load();
                  }}
                />
              ),
            },
          ]}
        />
      )}
    </Section>
  );
}
